import { defineStore, storeToRefs } from "pinia"
import { useAuthStore } from "./auth"
import { ref, watchEffect } from "vue"
import { apiClient } from "../http/http"

/** Global store for handling the credits balance.
 *
 * We need a global store as this can be modified and read by multiple components at
 * the same time.
 */
export const useCreditsStore = defineStore("credits", () => {
    const creditsRemaining = ref<number | null>(null)
    const creditsGranted = ref<number | null>(null)
    const creditsUsed = ref<number | null>(null)

    const authStore = useAuthStore()
    const { currentOrganization } = storeToRefs(authStore)

    const updateCurrentBalance = async () => {
        const response = await apiClient.get("/billing/credits/balance")
        creditsRemaining.value = response.credits_remaining as number | null
        creditsGranted.value = response.credits_granted as number | null
        creditsUsed.value = response.credits_used as number | null
    }

    watchEffect(() => {
        // When changing organization, update the balance to reflect the new organization's
        //  balance.
        if (currentOrganization.value) updateCurrentBalance()
    })

    // Polling
    let balancePollingInterval: number | null
    const startPollingCreditBalance = (pollingTime: number = 5 * 1_000) => {
        if (balancePollingInterval != null)
            throw "Balance polling already active, cannot start another poll."

        balancePollingInterval = setInterval(async () => {
            await updateCurrentBalance()
        }, pollingTime)
    }
    const stopPollingCreditBalance = () => {
        if (balancePollingInterval != null) {
            clearInterval(balancePollingInterval)
            // Reset the inteval ID after clearing
            balancePollingInterval = null
        }
    }

    return {
        creditsGranted,
        creditsRemaining,
        creditsUsed,
        updateCurrentBalance,
        startPollingCreditBalance,
        stopPollingCreditBalance,
    }
})
